import React from 'react';
import { Squirrel, Fish, Bird, Turtle, Worm, Leaf } from 'lucide-react';
import { BarChart, Bar, XAxis, ResponsiveContainer, Text } from 'recharts';
import Frog from 'src/assets/images/dashboard/amphibians-icon.svg?react';
import Archaea from 'src/assets/images/dashboard/archaea.svg?react';
import Bacteria from 'src/assets/images/dashboard/bacteria.svg?react';
import Fungi from 'src/assets/images/dashboard/fungi.svg?react';
import Protists from 'src/assets/images/dashboard/protists.svg?react';
import Loader from 'src/shared/components/loader/Loader';
import MetricCard from 'src/shared/components/metric-card/MetricCard';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { useSpeciesGroupsMetricsQuery } from 'src/app/dashboard/state/metrics/metricsGraphSlice';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';

const speciesGroupsIconDetails: { [key: string]: { icon: any; xValue: number } } = {
    Mammals: { icon: <Squirrel height={16} width={16} y={-11} />, xValue: 42 },
    Birds: { icon: <Bird height={16} width={16} y={-11} />, xValue: 28 },
    Fish: { icon: <Fish height={16} width={16} y={-11} />, xValue: 25 },
    Amphibians: { icon: <Frog height={16} width={16} y={-11} />, xValue: 50 },
    Reptiles: { icon: <Turtle height={16} width={16} y={-11} />, xValue: 35 },
    Invertebrates: { icon: <Worm height={16} width={16} y={-11} />, xValue: 55 },
    Plants: { icon: <Leaf height={16} width={16} y={-11} />, xValue: 30 },
    Fungi: { icon: <Fungi height={16} width={16} y={-11} />, xValue: 30 },
    Protists: { icon: <Protists height={16} width={16} y={-11} />, xValue: 35 },
    Bacteria: { icon: <Bacteria height={16} width={16} y={-11} />, xValue: 40 },
    Archaea: { icon: <Archaea height={16} width={16} y={-11} />, xValue: 40 },
};

const SpeciesGroupsMetrics = () => {
    const {
        currentProjectId,
        currentProjectSelectedHabitatAssayType: habitatAssayType,
        currentProjectSelectedDateRange,
    } = useProjectFilters();

    const { currentData: speciesGroups, isFetching } = useSpeciesGroupsMetricsQuery(
        {
            projectId: currentProjectId || '',
            fromDate: currentProjectSelectedDateRange?.startDate,
            toDate: currentProjectSelectedDateRange?.endDate,
            habitatAssay: habitatAssayType?.key || '',
        },
        {
            skip: !currentProjectId || !habitatAssayType || !currentProjectSelectedDateRange,
        }
    );

    if (isFetching) {
        return <Loader />;
    }

    if (!speciesGroups) {
        return null;
    }

    const chartData: { label: string; value: number }[] = Object.keys(speciesGroupsIconDetails).map(speciesGroup => {
        return {
            label: speciesGroup,
            value: speciesGroups.find(sg => sg.metric === speciesGroup)?.data || 0,
        };
    });

    // Add Other (Missing group) bar if count is more than 0
    const otherCount = speciesGroups.find(sg => sg.metric === 'Other')?.data || 0;
    if (otherCount > 0) {
        chartData.push({
            label: 'Missing group',
            value: otherCount,
        });
    }

    const content = (
        <div className='h-[181px]'>
            <ResponsiveContainer width='100%' height={181}>
                <BarChart data={chartData} barSize={70} margin={{ top: 15, bottom: 0, right: 0 }}>
                    <XAxis dataKey='label' tickLine={false} fontSize={12} stroke={'#D9D9D9'} tick={<CustomTick />} interval={0} />
                    <Bar dataKey='value' fill='#0FC0C7' radius={[8, 8, 0, 0]} label={{ position: 'top', fontSize: 14, fill: '#666' }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    return (
        <MetricCard
            header='Species Groups'
            helpText={<QuestionButtonHelp type='api' placement='right' slug='species-groups' detailedExplainerSlug='species-groups-te' />}
            hideBackgroundImage={true}
            content={content}
            classes={{ content: 'block w-full' }}
        />
    );
};

// Used `any` type because the recharts tick expected the props to be `any`
const CustomTick = ({ x, y, payload }: any) => {
    const tickDetails = speciesGroupsIconDetails[payload.value] || { icon: null, xValue: 63 };
    return (
        <g transform={`translate(${x - tickDetails.xValue},${y + 12})`} className='text-xs text-grey-80'>
            {tickDetails.icon}
            <Text x={21} y={1}>
                {payload.value}
            </Text>
        </g>
    );
};

export default SpeciesGroupsMetrics;
